<template>
  <div
    ref="cardRoot"
    :class="['card']"
    @mouseenter.self="onMouseenter"
    @mouseleave.self="onMouseleave"
  >
    <div class="unveil">
      <slot name="cover">
        <Resource
          :src="data?.cover_url ? data?.cover_url : PLACEHOLDER"
          :title="data?.name"
          :class="[
            'w-full',
            data?.cover_url && data.resolution === '1080x1920' ? 'object-contain' : 'object-cover',
          ]"
          :transform-resource="
            url => transformImage(url, autoScale ? { width: 600 } : { height: 600 })
          "
        />
        <video
          :src="showVideo ? data?.hover_video || data?.preview_video : ''"
          autoplay
          loop
          muted
          playsinline
          :class="['w-full', 'h-full', 'object-contain', { show: videoVisible }]"
          @loadstart="onLoadStart"
          @loadedmetadata="onLoaded"
        />
      </slot>
      <div class="Card--floats">
        <slot name="floats"> </slot>
      </div>
    </div>
    <div class="Card--panel" @click.stop>
      <div v-if="$slots.operation" class="Card--operation">
        <slot name="operation"> </slot>
      </div>
      <div class="Card--detail">
        <slot name="detail"> </slot>
      </div>
    </div>
    <div class="Card--addons" @click.stop></div>
  </div>
</template>

<script lang="ts" setup>
import type { Template } from '~/api/interface';
import { PLACEHOLDER } from '~/utils/constant';
import { transformImage } from '~/utils/index.ts';

const props = withDefaults(
  defineProps<{
    data?: Template;
    autoScale?: boolean;
  }>(),
  {
    data: undefined,
    autoScale: true,
  },
);

const cardRoot = ref<HTMLDivElement>();

provide('card-item', props.data);

defineEmits(['change']);
const showVideo = ref(false);
const videoVisible = ref(false);
let timer: any = null;

const onLoadStart = () => (videoVisible.value = false);
const onLoaded = () => (videoVisible.value = true);

const onMouseenter = () => {
  timer = setTimeout(() => {
    showVideo.value = true;
  }, 300);
};

const onMouseleave = () => {
  showVideo.value = false;
  clearTimeout(timer);
  timer = null;
};
</script>

<style lang="scss" scoped>
.card {
  @apply w-full relative rounded-[10px] outline-0 flex flex-col box-content;
  transition: transform 0.3s ease-in-out;
  border: 1px solid var(--back-3);

  &:hover {
    padding: 7px;
    box-shadow: 0px 1px 18px 0px rgba(4, 14, 48, 0.1);
    transform: translate3d(0, -3px, 0);
    left: -7px;
    top: -7px;
    z-index: 2;
    .Card--panel .Card--operation {
      display: flex;
    }
  }

  &:hover {
    .unveil img,
    .unveil video {
      transform: scale(1.05);
    }
  }

  .unveil {
    @apply relative bg-back-4 overflow-hidden cursor-pointer flex-1;
    width: 100%;
    border-bottom: 0;
    backface-visibility: hidden;
    border-radius: 8px 8px 0 0;
    position: relative;

    .Card--floats {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }

    img,
    video {
      @apply absolute w-full h-full top-0 left-0;
    }

    video.show {
      background-color: #efefef;
    }
  }

  .Card--panel {
    position: relative;
    padding: 12px;
    .Card--operation {
      display: none;
    }
  }

  .Card--operation {
    background: var(--back-1);
    width: 100%;
    position: absolute;
    gap: 12px;
    left: 0;
    bottom: 30px;
    padding: 10px 12px;
    display: flex;
    &:deep(> button, > div) {
      padding: 0;
    }
  }
}
</style>
